import { type ReactNode, type FC, useRef } from "react";

import {
  Box,
  type BoxProps,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Column,
  useDisclosure,
  useOutsideClick,
} from "@hightouch/htui";

import { useManualPortal } from "src/hooks/use-manual-portal";

import { HStack } from "./shared";
import { ErrorMessage } from "./error-message";

const MAX_FILTER_WIDTH = 460;

type Props = {
  children: ReactNode;
  closeOnBlur?: boolean;
  content: ReactNode | (({ onClose }: { onClose: () => void }) => ReactNode);
  error?: ReactNode | string;
  isInvalid?: boolean;
  fullWidth?: boolean;
  isDisabled?: boolean;
  isEmpty?: boolean;
  showButtonBoxShadow?: boolean;
  variant?: "light" | "dark";
  onClose?: () => void;
  initialFocusRef?: React.ComponentProps<typeof Popover>["initialFocusRef"];
  // `BoxProps` includes the native `content?: string` HTML attribute (via
  // Chakra v2.10), which would intersect with our `content` prop above and
  // reduce it to `... & string`.
} & Omit<BoxProps, "content">;

export const Filter: FC<Readonly<Props>> = ({
  children,
  closeOnBlur = false,
  content,
  error,
  isInvalid = false,
  fullWidth = false,
  isDisabled = false,
  showButtonBoxShadow = true,
  isEmpty,
  variant = "light",
  onClose: closeFn,
  initialFocusRef,
  ...props
}) => {
  // Need to attach the content to the DOM manually
  // because chakra Portal's `containerRef` renders the
  // content inside the main tree.
  const containerRef = useManualPortal();
  const triggerRef = useRef<HTMLDivElement>(null);
  const { isOpen, onToggle, onClose } = useDisclosure({
    onClose: closeFn,
  });

  const applyEmptyFieldStyles = isEmpty && !isOpen && !isInvalid && !error;

  useOutsideClick({
    enabled: isOpen,
    ref: containerRef,
    handler: (event) => {
      // The trigger element is inherently outside the popover content container
      if (triggerRef.current?.contains(event.target as Node)) {
        return; // Don't close the popover
      }

      // React-select sets up its own anchor. If a react-select
      // is rendered as a child in the filter then clicking inside of the
      // portal will be seen as clicking outside of the filter.
      const reactSelectPortal = document.getElementById("portalAnchor");
      if (reactSelectPortal?.contains(event.target as Node)) {
        return;
      }

      onClose();
    },
  });

  if (!containerRef.current) return null;

  return (
    <Column align="flex-start" minWidth={0} {...props}>
      <Popover
        isLazy
        returnFocusOnClose
        closeOnBlur={closeOnBlur}
        isOpen={isOpen}
        onClose={onClose}
        placement="bottom"
        initialFocusRef={initialFocusRef}
      >
        <PopoverTrigger>
          <Box
            as={Button}
            ref={triggerRef}
            isDisabled={isDisabled}
            width={fullWidth ? "100%" : undefined}
            sx={{
              border: "none",
              p: 0,
              svg: {
                color: undefined,
              },
              _disabled:
                variant === "light"
                  ? {
                      div: {
                        bg: "gray.200",
                        color: "text.disabled",
                        "*": {
                          color: "text.disabled",
                        },
                        _hover: {
                          borderColor: "base.border",
                        },
                      },
                    }
                  : {
                      div: {
                        _hover: {
                          borderColor: "base.border",
                          bg: "base.background",
                        },
                      },
                    },
            }}
            onClick={onToggle}
          >
            <HStack
              _hover={{
                borderColor:
                  isInvalid || error
                    ? "danger.base"
                    : isOpen
                      ? "primary.base"
                      : "base.borderHover",
                bg: variant === "dark" ? "base.divider" : "white",
              }}
              _active={
                !isDisabled
                  ? {
                      borderWidth: "hairline",
                      borderStyle: "solid",
                      borderColor:
                        isInvalid || error ? "danger.base" : "primary.base",
                      boxShadow: isInvalid || error ? "outlineRed" : "outline",
                    }
                  : {}
              }
              px={2}
              gap={1}
              alignItems="center"
              height="32px"
              fontWeight="normal"
              borderWidth="hairline"
              borderStyle="solid"
              boxShadow={
                showButtonBoxShadow
                  ? isInvalid || error
                    ? "outlineRed"
                    : isOpen
                      ? "outline"
                      : "buttonShadowLight"
                  : "none"
              }
              borderRadius="lg"
              maxWidth={`${MAX_FILTER_WIDTH}px`}
              overflow="hidden"
              // TODO(HTUI-393): stop animating box-shadow (swept in by "common")
              transitionProperty="common"
              transitionDuration="fast"
              transitionTimingFunction="easeOutQuad"
              whiteSpace="nowrap"
              {...(applyEmptyFieldStyles
                ? {
                    borderStyle: "dashed",
                    borderColor: "teal.border",
                    bg: "teal.background",
                  }
                : {
                    bg:
                      variant === "dark"
                        ? "base.background"
                        : "base.lightBackground",
                    borderColor:
                      isInvalid || error
                        ? "danger.base"
                        : isOpen
                          ? "primary.base"
                          : "base.border",
                  })}
              sx={
                applyEmptyFieldStyles
                  ? {
                      span: {
                        color: "primary.base",
                        fontWeight: "medium",
                      },
                    }
                  : {}
              }
            >
              {children}
            </HStack>
          </Box>
        </PopoverTrigger>

        <Portal containerRef={containerRef}>
          {/* `containerRef` overrides the containerRef from hightouch ui. This hack is needed until the z-index problems are addressed. */}
          <Box
            sx={{
              "& .chakra-popover__popper": {
                zIndex: "popover",
              },
            }}
          >
            <PopoverContent
              borderRadius="2xl"
              boxShadow="md"
              width="max-content"
            >
              <Column
                align="flex-start"
                flex={1}
                maxWidth="fit-content"
                minWidth={0}
                p={2}
              >
                <HStack gap={2} sx={{ overflowX: "auto" }}>
                  {typeof content === "function"
                    ? content({ onClose })
                    : content}
                </HStack>
              </Column>
            </PopoverContent>
          </Box>
        </Portal>
      </Popover>

      {error && (
        <Box width="100%" sx={{ contain: "inline-size" }}>
          <ErrorMessage whiteSpace="normal" overflowWrap="anywhere">
            {error}
          </ErrorMessage>
        </Box>
      )}
    </Column>
  );
};
